// One-line messages that go away by themselves: a done, a warning the
// model would have read aloud, an error with its reason. An optional
// action ("Geri al") rides on the line.

import {useCallback, useMemo, useRef, useState} from "react";
import type {ReactNode} from "react";
import {ToastCtx, type Toast, type ToastApi} from "./toast-context";

const LIFE_MS = 7000;

export function ToastProvider({children}: {children: ReactNode}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  const remove = useCallback((id: number) =>
    setToasts((ts) => ts.filter((t) => t.id !== id)), []);
  const push = useCallback((t: Omit<Toast, "id">) => {
    const id = ++seq.current;
    setToasts((ts) => [...ts.slice(-4), {...t, id}]);
    // Errors and lines with an action stay longer; the eye needs them.
    const life = t.kind === "error" || t.action ? LIFE_MS * 2 : LIFE_MS;
    setTimeout(() => remove(id), life);
  }, [remove]);
  const api = useMemo<ToastApi>(() => ({
    ok: (text, action) => push({kind: "ok", text, action}),
    warn: (text, action) => push({kind: "warn", text, action}),
    error: (text) => push({kind: "error", text}),
  }), [push]);
  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}
            role={t.kind === "error" ? "alert" : "status"}>
            <span className="toast-text">{t.text}</span>
            {t.action && (
              <button className="toast-action" onClick={() => {
                void t.action?.run();
                remove(t.id);
              }}>{t.action.label}</button>
            )}
            <button className="toast-close" aria-label="Kapat" onClick={() => remove(t.id)}>
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
